import { format } from 'date-fns';
import { useDashboard } from '@/hooks/useDashboard';
import { CaptureButton } from '@/components/capture/CaptureButton';
import { PinnedCard } from '@/components/cards/PinnedCard';
import { useDayState } from '@/hooks/useDayState';
import { useClock } from '@/hooks/useClock';
import { Link } from 'react-router-dom';

const GREETING: Record<string, string> = {
  morning: 'Good morning',
  afternoon: 'Good afternoon',
  evening: 'Good evening',
  night: 'Goodnight',
};

export function PocketRoute() {
  const { data } = useDashboard();
  const dayState = useDayState();
  const now = useClock(30_000);
  if (!data) return null;

  const greet = GREETING[dayState] ?? 'Hello';
  const pins = data.pins.slice(0, 5);

  return (
    <div className="min-h-screen px-5 py-8 flex flex-col gap-6 max-w-md mx-auto">
      <header className="flex flex-col gap-1.5">
        <span className="text-eyebrow uppercase tracking-[0.18em] text-inkFaded">
          {format(now, 'EEEE d MMMM')} · {format(now, 'HH:mm')}
        </span>
        <h1 className="font-serif italic text-display-md text-ink leading-tight">
          {greet}, <span className="text-tomato">family</span>.
        </h1>
        <p className="font-serif italic text-body-lg text-inkSoft mt-1 leading-snug">
          {data.headline.text}
        </p>
      </header>

      <section className="flex gap-3">
        <div
          className="flex-1 rounded-md p-4 text-ink shadow-sm"
          style={{ background: 'linear-gradient(135deg, #FFD580 0%, #FFA952 50%, #FF7849 100%)' }}
        >
          <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-ink/55">
            Today
          </div>
          <div className="font-serif text-[36px] text-ink leading-none mt-1">
            {data.weather.temperatureC}°
          </div>
          <div className="text-[12px] text-ink/70 mt-1 capitalize">
            {data.weather.condition}
            {data.weather.sub ? ` · ${data.weather.sub}` : ''}
          </div>
        </div>
        <div className="flex-1 rounded-md p-4 bg-card shadow-sm">
          <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-inkFaded">
            On the wall
          </div>
          <div className="font-serif text-[36px] text-ink leading-none mt-1">
            {data.agenda.length}
          </div>
          <div className="text-[12px] text-inkFaded mt-1">
            {data.agenda.length === 1 ? 'event' : 'events'} today
          </div>
        </div>
      </section>

      <CaptureButton variant="large" />

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-eyebrow uppercase tracking-[0.18em] text-inkFaded">Pinned</h2>
          <Link to="/wall" className="text-body-sm text-tomato">
            see the wall →
          </Link>
        </div>
        {pins.length === 0 ? (
          <p className="text-body-sm text-inkGhost font-serif italic">The board is clear.</p>
        ) : (
          pins.map((pin) => <PinnedCard key={pin.id} pin={pin} compact />)
        )}
      </section>
    </div>
  );
}
